import React from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";

import PsychologistDetailsView from "../components/ui/PsychologistDetailsView";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { psychologistsApi } from "../api/psychologistsApi";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";
import { getSiteOrigin, joinUrl } from "../utils/seo";
import { buildPsychologistProfilePath, normalizePsychologistLogin, parsePsychologistIdToken } from "../utils/psychologistProfile";

function safeText(v) {
  return String(v ?? "").trim();
}

function unwrapPayload(raw) {
  if (raw && typeof raw === "object") {
    if (raw.ok === true && raw.item && typeof raw.item === "object") return raw.item;
    if (raw.item && typeof raw.item === "object") return raw.item;
  }
  return raw;
}

function roleLabelRu(roleRaw) {
  const role = String(roleRaw || "")
    .replace(/^ROLE_/, "")
    .toUpperCase();
  if (role === "ADMIN") return "Администратор";
  if (role === "PSYCHOLOGIST") return "Психолог";
  if (role === "MODERATOR") return "Модератор";
  return "Клиент";
}

export default function PsychologistProfile() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, me, role, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();
  const routeToken = React.useMemo(() => {
    const base = safeText(params?.id);
    const slug = safeText(params?.slug);
    if (base && slug) return `${base}-${slug}`;
    return base;
  }, [params?.id, params?.slug]);
  const routeTokenId = React.useMemo(() => parsePsychologistIdToken(routeToken), [routeToken]);
  const routeTokenLogin = React.useMemo(() => normalizePsychologistLogin(routeToken), [routeToken]);
  const routeTokenNormalized = React.useMemo(
    () => (routeTokenId != null ? String(routeTokenId) : routeTokenLogin || ""),
    [routeTokenId, routeTokenLogin]
  );
  const origin = React.useMemo(() => getSiteOrigin(), []);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const [loading, setLoading] = React.useState(true);
  const [errorText, setErrorText] = React.useState("");
  const [profile, setProfile] = React.useState(null);
  const profileRoutePath = React.useMemo(() => buildPsychologistProfilePath(profile), [profile]);
  const profileRouteToken = React.useMemo(
    () => safeText(profileRoutePath).replace(/^\/psychologist\//, ""),
    [profileRoutePath]
  );

  const canonicalUrl = React.useMemo(() => {
    if (!origin) return "";
    const canonicalPath = profileRoutePath || (routeTokenNormalized ? `/psychologist/${routeTokenNormalized}` : "");
    if (!canonicalPath) return "";
    return joinUrl(origin, canonicalPath);
  }, [origin, profileRoutePath, routeTokenNormalized]);

  React.useEffect(() => {
    let alive = true;

    if (!routeTokenNormalized) {
      setLoading(false);
      setProfile(null);
      setErrorText("Некорректный адрес профиля психолога.");
      return () => {
        alive = false;
      };
    }

    setLoading(true);
    setErrorText("");
    setProfile(null);

    psychologistsApi
      .resolveProfile(routeTokenNormalized)
      .then((raw) => {
        if (!alive) return;
        const item = unwrapPayload(raw);
        if (!item || typeof item !== "object") {
          setErrorText("Профиль психолога пока недоступен.");
          setProfile(null);
          return;
        }
        setProfile(item);
      })
      .catch((error) => {
        if (!alive) return;
        const status = error?.status ?? error?.response?.status;
        if (status === 404) setErrorText("Психолог не найден.");
        else setErrorText(error?.message || "Не удалось загрузить профиль психолога.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [routeTokenNormalized]);

  React.useEffect(() => {
    // Reset both page and shell scrollers when profile route changes on mobile.
    const resetScroll = () => {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      if (typeof document !== "undefined") {
        const shellMain = document.querySelector(".psych-profile-auth-shell__main");
        const shellContent = document.querySelector(".psych-profile-auth-shell__content");
        if (shellMain && "scrollTop" in shellMain) shellMain.scrollTop = 0;
        if (shellContent && "scrollTop" in shellContent) shellContent.scrollTop = 0;
      }
    };

    resetScroll();
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(resetScroll);
    }
  }, [routeTokenNormalized]);

  React.useEffect(() => {
    if (!profileRouteToken || !routeTokenNormalized) return;
    if (profileRouteToken.toLowerCase() === routeTokenNormalized.toLowerCase()) return;
    navigate(`/psychologist/${encodeURIComponent(profileRouteToken)}${location.search || ""}${location.hash || ""}`, {
      replace: true,
    });
  }, [location.hash, location.search, navigate, profileRouteToken, routeTokenNormalized]);

  const pageTitle = React.useMemo(() => {
    const name = safeText(profile?.name);
    return name ? `${name} — психолог | Бюро Души` : "Профиль психолога — Бюро Души";
  }, [profile?.name]);

  const description = React.useMemo(() => {
    const about = safeText(profile?.about || profile?.headline);
    if (!about) return "Детальный профиль психолога: направления работы, форматы сессий, отзывы и запись.";
    if (about.length <= 170) return about;
    return `${about.slice(0, 169)}…`;
  }, [profile?.about, profile?.headline]);

  const normalizedRole = React.useMemo(
    () => String(role || "").replace(/^ROLE_/, "").toUpperCase(),
    [role]
  );

  const shellUserName =
    me?.name || me?.fullName || me?.login || me?.email || "Пользователь";

  const canOpenLiteratureEditor =
    normalizedRole === "ADMIN" ||
    normalizedRole === "PSYCHOLOGIST" ||
    normalizedRole === "MODERATOR";

  const shellTitle = React.useMemo(() => {
    if (loading) return "Загружаем профиль";
    if (errorText) return "Профиль психолога";
    return safeText(profile?.name || "Профиль психолога");
  }, [loading, errorText, profile?.name]);

  const renderAuthedShell = React.useCallback(
    (content) => {
      if (!isAuthenticated) {
        return <div className="psych-profile-v7-page">{content}</div>;
      }
      return (
        <div className="podcasts-hub psych-profile-auth-shell">
          <div className="shell shell--shared podcasts-shell">
            <AppSidebarV2
              activeKey=""
              mobileOpen={mobileSidebarOpen}
              onCloseMobile={() => setMobileSidebarOpen(false)}
              userName={shellUserName}
              roleLabel={roleLabelRu(normalizedRole)}
              canOpenLiteratureEditor={canOpenLiteratureEditor}
              notificationsBadge={unreadNotificationsCount}
              onLogout={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
            />

            <button
              className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
              type="button"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Закрыть меню"
            />

            <main className="main podcasts-main psych-profile-auth-shell__main">
              <header className="podcasts-topbar psych-profile-topbar">
                <div className="podcasts-topbar__left">
                  <button
                    type="button"
                    className="podcasts-topbar__menu-btn"
                    onClick={() => setMobileSidebarOpen(true)}
                    aria-label="Открыть меню"
                  >
                    ☰
                  </button>
                  <div className="psych-profile-topbar__titles">
                    <div className="psych-profile-topbar__title">
                      Профиль специалиста
                    </div>
                    <div className="psych-profile-topbar__subtitle">{shellTitle}</div>
                  </div>
                </div>

                <div className="podcasts-topbar__right">
                  <button
                    type="button"
                    className="podcasts-btn podcasts-btn--mint psych-profile-topbar__catalog-btn"
                    onClick={() => navigate("/catalog")}
                  >
                    К каталогу
                  </button>
                  <button
                    type="button"
                    className="podcasts-topbar__avatar psych-profile-topbar__notif-btn"
                    onClick={() => navigate("/notifications")}
                    aria-label="Уведомления"
                  >
                    🔔
                    {Number(unreadNotificationsCount) > 0 ? (
                      <span className="psych-profile-topbar__notif-dot" />
                    ) : null}
                  </button>
                </div>
              </header>

              <section className="psych-profile-auth-shell__content">{content}</section>
            </main>
          </div>
        </div>
      );
    },
    [
      isAuthenticated,
      mobileSidebarOpen,
      shellUserName,
      normalizedRole,
      canOpenLiteratureEditor,
      unreadNotificationsCount,
      logout,
      navigate,
      shellTitle,
    ]
  );

  if (loading) {
    return renderAuthedShell(
      <>
        <Helmet>
          <title>Загрузка профиля психолога…</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="psych-profile-empty">
          <div className="psych-profile-empty__title">Загружаем профиль</div>
          <div className="psych-profile-empty__text">Подождите пару секунд, данные специалиста подтягиваются.</div>
        </div>
      </>
    );
  }

  if (errorText || !profile) {
    return renderAuthedShell(
      <>
        <Helmet>
          <title>Профиль психолога</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="psych-profile-empty">
          <div className="psych-profile-empty__title">{errorText || "Профиль психолога недоступен"}</div>
          <div className="psych-profile-empty__text">
            Проверьте ссылку или откройте каталог, чтобы выбрать другого специалиста.
          </div>
          <Link className="psych-profile-empty__link" to="/catalog">
            Вернуться в каталог
          </Link>
        </div>
      </>
    );
  }

  return renderAuthedShell(
    <>
      <Helmet prioritizeSeoTags>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        <meta name="robots" content="index, follow" />
      </Helmet>

      <PsychologistDetailsView
        psychologist={profile}
        shareUrl={canonicalUrl}
        shareTitle={safeText(profile?.name || "Психолог Бюро Души")}
      />
    </>
  );
}
